import { Box, Button, Flex, Heading, IconButton, Spinner, Text, useDisclosure, useToast } from '@chakra-ui/react'
import { format } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'
import { RiFullscreenExitLine, RiFullscreenLine } from 'react-icons/ri'
import { useMutation } from 'react-query'
import { FinancialRecurringFilters, FinancialRecurringTransaction } from '../../Interfaces/financialRecurring'
import {
  createFinancialTransaction,
  deleteFinancialTransaction,
  markFinancialTransactionAsPaid,
  updateFinancialTransaction,
} from '../../services/financialRecurring'
import { useClients } from '../../services/hooks/useClients'
import { useEmployees } from '../../services/hooks/useEmployees'
import { usePaymentConditions } from '../../services/hooks/usePaymentConditions'
import { usePaymentMethods } from '../../services/hooks/usePaymentMethods'
import { useProviders } from '../../services/hooks/useProviders'
import { useFinancialTransactions } from '../../services/hooks/useRecurringTransactions'
import { DeleteTransactionModal } from './components/DeleteTransactionModal'
import { FiltersToolbar } from './components/FiltersToolbar'
import { MarkAsPaidModal } from './components/MarkAsPaidModal'
import { SummaryCards } from './components/SummaryCards'
import { TransactionFormModal } from './components/TransactionFormModal'
import { TransactionsTable } from './components/TransactionsTable'

const DEFAULT_PER_PAGE = 20

const createInitialFilters = (): FinancialRecurringFilters => {
  const now = new Date()
  const start = format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd')
  const end = format(new Date(now.getFullYear(), now.getMonth() + 1, 0), 'yyyy-MM-dd')

  return {
    start_date: start,
    end_date: end,
    reference_type: 'MOVIMENTO',
    status: 'TODOS',
    type: 'TODOS',
    category: 'EMPRESA',
    payment_method_id: '',
    page: 1,
    per_page: DEFAULT_PER_PAGE,
  }
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value ?? 0)

const formatDate = (value?: string | null) => {
  if (!value) return '-'
  
  // Se a data vier no formato yyyy-MM-dd, parsear como data local para evitar problemas de timezone
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number)
    const date = new Date(year, month - 1, day) // month - 1 porque Date usa 0-11 para meses
    if (Number.isNaN(date.getTime())) return '-'
    return format(date, 'dd/MM/yyyy')
  }
  
  // Para outros formatos, usar conversão normal
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return format(date, 'dd/MM/yyyy')
}

export default function Financial() {
  const toast = useToast()
  const formDisclosure = useDisclosure()
  const markPaidDisclosure = useDisclosure()
  const deleteDisclosure = useDisclosure()

  const [filters, setFilters] = useState<FinancialRecurringFilters>(createInitialFilters)
  const [uiFilters, setUiFilters] = useState<FinancialRecurringFilters>(createInitialFilters)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [selectedTransaction, setSelectedTransaction] = useState<FinancialRecurringTransaction | null>(null)
  const [isMaximized, setIsMaximized] = useState(false)

  const { data, isLoading, isFetching, refetch } = useFinancialTransactions(filters)

  const { data: clientsData } = useClients<{ label: string; value: number }[]>({ isSelect: true, staleTime: true })
  const { data: providersData } = useProviders<{ label: string; value: number }[]>(true, {})
  const { data: employeesData } = useEmployees<{ label: string; value: number }[]>({ isSelect: true, staleTime: true })

  const clientOptions = clientsData?.clients || []
  const providerOptions = providersData?.providers || []
  const employeeOptions = employeesData?.employees || []

  const { data: paymentMethodsData } = usePaymentMethods({ is_active: true, limit: 100 })
  const { data: paymentConditionsData } = usePaymentConditions({ is_active: true, limit: 100 })

  const filteredData = useMemo(() => {
    if (!data?.data) return []

    return data.data.filter((transaction) => {
      const isRecurring = Boolean(transaction.is_recurring)

      if (uiFilters.ui_nature === 'RECEITAS' && transaction.type !== 'CREDITO') {
        return false
      }
      if (uiFilters.ui_nature === 'DESPESAS' && transaction.type !== 'DEBITO') {
        return false
      }
      if (uiFilters.ui_recurrence === 'RECORRENTES' && !isRecurring) {
        return false
      }
      if (uiFilters.ui_recurrence === 'AVULSOS' && isRecurring) {
        return false
      }
      return true
    })
  }, [data?.data, uiFilters.ui_nature, uiFilters.ui_recurrence])

  const clientsOptions: any = clientsData?.clients ?? []
  const paymentMethodOptions: any = useMemo(
    () =>
      (paymentMethodsData?.paymentMethods ?? []).map((method) => ({
        label: method.name,
        value: method.id,
      })),
    [paymentMethodsData?.paymentMethods],
  )

  const paymentConditionOptions: any = useMemo(
    () =>
      (paymentConditionsData?.paymentConditions ?? []).map((condition) => ({
        label: condition.name,
        value: condition.id,
        recurrence: condition.recurrence,
      })),
    [paymentConditionsData?.paymentConditions],
  )

  useEffect(() => {
    setUiFilters(filters)
  }, [])

  const createMutation = useMutation(createFinancialTransaction, {
    onSuccess: async () => {
      toast({ description: 'Lançamentos salvos com sucesso.', status: 'success' })
      await refetch()
      formDisclosure.onClose()
    },
    onError: () => {
      toast({ description: 'Não foi possível criar o lançamento.', status: 'error' })
    },
  })

  const updateMutation = useMutation(
    ({ id, payload }: { id: number; payload: Parameters<typeof updateFinancialTransaction>[1] }) =>
      updateFinancialTransaction(id, payload),
    {
      onSuccess: async () => {
        toast({ description: 'Lançamento atualizado com sucesso.', status: 'success' })
        await refetch()
        formDisclosure.onClose()
      },
      onError: () => {
        toast({ description: 'Não foi possível atualizar o lançamento.', status: 'error' })
      },
    },
  )

  const deleteMutation = useMutation(
    ({ id, scope }: { id: number; scope?: 'single' | 'group' | 'end' }) =>
      deleteFinancialTransaction(id, { scope }),
    {
      onSuccess: async () => {
        toast({ description: 'Lançamento removido com sucesso.', status: 'success' })
        await refetch()
        deleteDisclosure.onClose()
        setSelectedTransaction(null)
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error?.message || 'Não foi possível remover o lançamento.'
        toast({ description: errorMessage, status: 'error', duration: 5000 })
        console.error('Erro ao excluir lançamento:', error)
      },
    }
  )

  const markAsPaidMutation = useMutation(
    ({ id, payload }: { id: number; payload: Parameters<typeof markFinancialTransactionAsPaid>[1] }) =>
      markFinancialTransactionAsPaid(id, payload),
    {
      onSuccess: async () => {
        const isReceita = selectedTransaction?.type === 'CREDITO' || selectedTransaction?.type === 'BOLETO'
        toast({
          description: isReceita ? 'Recebimento registrado com sucesso.' : 'Pagamento registrado com sucesso.',
          status: 'success',
        })
        await refetch()
        markPaidDisclosure.onClose()
      },
      onError: () => {
        const isReceita = selectedTransaction?.type === 'CREDITO' || selectedTransaction?.type === 'BOLETO'
        toast({
          description: isReceita
            ? 'Não foi possível registrar o recebimento.'
            : 'Não foi possível registrar o pagamento.',
          status: 'error',
        })
      },
    },
  )

  const handleFiltersChange = (partial: Partial<FinancialRecurringFilters>) => {
    setUiFilters((prev) => ({ ...prev, ...partial }))
  }

  const handleApplyFilters = () => {
    setFilters((prev) => ({
      ...prev,
      ...uiFilters,
      page: 1,
    }))
  }

  const handleClearFilters = () => {
    const initial = createInitialFilters()
    setUiFilters(initial)
    setFilters(initial)
  }

  const handleCreateClick = () => {
    setFormMode('create')
    setSelectedTransaction(null)
    formDisclosure.onOpen()
  }

  const handleEdit = (transaction: FinancialRecurringTransaction) => {
    setFormMode('edit')
    setSelectedTransaction(transaction)
    formDisclosure.onOpen()
  }

  const handleMarkAsPaid = (transaction: FinancialRecurringTransaction) => {
    setSelectedTransaction(transaction)
    markPaidDisclosure.onOpen()
  }

  const handleDelete = (transaction: FinancialRecurringTransaction) => {
    setSelectedTransaction(transaction)
    deleteDisclosure.onOpen()
  }

  const handleDeleteSubmit = async (scope: 'single' | 'group' | 'end') => {
    if (!selectedTransaction) return

    const transactionId = selectedTransaction.recurring_transaction_id ?? selectedTransaction.id

    try {
      await deleteMutation.mutateAsync({
        id: transactionId,
        scope,
      })
    } catch (error) {
      // Erro já é tratado no onError da mutation
    }
  }

  const handleFormSubmit = async (
    payload:
      | { mode: 'create'; data: Parameters<typeof createFinancialTransaction>[0] }
      | { mode: 'edit'; id: number; data: Parameters<typeof updateFinancialTransaction>[1] },
  ) => {
    if (payload.mode === 'create') {
      await createMutation.mutateAsync(payload.data)
    } else {
      await updateMutation.mutateAsync({ id: payload.id, payload: payload.data })
    }
  }

  const handleStopRecurrence = async (id: number) => {
    const confirmed = window.confirm('Deseja encerrar a recorrência deste lançamento?')
    if (!confirmed) return

    await updateMutation.mutateAsync({
      id,
      payload: { is_recurring: false },
    })
  }

  const handleMarkAsPaidSubmit = async ({
    id,
    payment_date,
    interest_value,
    paid_value,
    payment_method_id,
    payment_condition_id,
  }: {
    id: number
    payment_date: string
    interest_value?: number
    paid_value?: number
    payment_method_id?: number | null
    payment_condition_id?: number | null
  }) => {
    await markAsPaidMutation.mutateAsync({
      id,
      payload: { payment_date, interest_value, paid_value, payment_method_id, payment_condition_id },
    })
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  const handleExport = (format: 'csv' | 'pdf') => {
    const rows = data?.data ?? []
    if (!rows.length) {
      toast({ description: 'Nenhum lançamento para exportar.', status: 'info' })
      return
    }

    if (format === 'csv') {
      const header = [
        'Documento',
        'Tipo',
        'Nº OS',
        'Categoria',
        'Cliente',
        'Movimento',
        'Vencimento',
        'Pagamento',
        'Valor',
        'Juros/Multa',
        'Valor Pago',
        'Situação',
        'Forma Pagamento',
      ]

      const csvRows = rows.map((item) => [
        item.document_number,
        item.type,
        item.id_os ?? '',
        item.category,
        item.client?.name || item.client?.company_name || '',
        formatDate(item.movement_date),
        formatDate(item.due_date),
        formatDate(item.payment_date),
        formatCurrency(item.value),
        formatCurrency(item.interest_value),
        item.paid_value ? formatCurrency(item.paid_value) : '-',
        item.status,
        item.paymentMethod?.name || '',
      ])

      const csvContent = [header, ...csvRows].map((row) => row.join(';')).join('\n')
      // Adicionar BOM UTF-8 para garantir que o arquivo seja reconhecido como UTF-8 (especialmente no Excel)
      const csvWithBOM = '\uFEFF' + csvContent
      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=UTF-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'lancamentos_financeiros.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      return
    }

    toast({ description: 'Exportação em PDF disponível em breve.', status: 'info' })
  }

  const AnyBox: any = Box
  const AnyFlex: any = Flex
  const AnyHeading: any = Heading
  const AnyText: any = Text
  const AnySpinner: any = Spinner
  const AnyButton: any = Button
  const AnyIconButton: any = IconButton

  return (
    <AnyBox
      flex="1"
      borderRadius="8"
      bg="gray.900"
      p={8}
      position={isMaximized ? 'fixed' : 'relative'}
      top={isMaximized ? 0 : 'auto'}
      left={isMaximized ? 0 : 'auto'}
      right={isMaximized ? 0 : 'auto'}
      bottom={isMaximized ? 0 : 'auto'}
      zIndex={isMaximized ? 1000 : 'auto'}
      width={isMaximized ? '100vw' : 'auto'}
      height={isMaximized ? '100vh' : 'auto'}
      overflowY={isMaximized ? 'auto' : 'visible'}>
      <AnyFlex align="center" justify="space-between" mb={6}>
        <AnyFlex align="center" gap={3}>
          <AnyHeading size="lg" fontWeight="normal">
            Lançamentos Financeiros
            {!isLoading && isFetching && <AnySpinner size="sm" color="gray.500" ml={4} />}
          </AnyHeading>
          <AnyIconButton
            aria-label={isMaximized ? 'Minimizar' : 'Maximizar'}
            icon={isMaximized ? <RiFullscreenExitLine /> : <RiFullscreenLine />}
            size="sm"
            variant="ghost"
            colorScheme="pink"
            onClick={() => setIsMaximized(!isMaximized)}
            _hover={{ bg: 'gray.700' }}
          />
        </AnyFlex>
        {!isMaximized && <AnyText color="gray.400">Gerencie lançamentos e recorrências financeiras</AnyText>}
      </AnyFlex>

      <SummaryCards totals={data?.meta.totals} />

      <FiltersToolbar
        filters={uiFilters}
        onFiltersChange={handleFiltersChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        onCreate={handleCreateClick}
        onExport={handleExport}
        paymentMethodOptions={paymentMethodOptions}
        clientOptions={clientOptions}
        providerOptions={providerOptions}
        employeeOptions={employeeOptions}
      />

      {isLoading ? (
        <AnyFlex justify="center" py={10}>
          <AnySpinner />
        </AnyFlex>
      ) : (
        <TransactionsTable
          data={filteredData}
          isLoading={isFetching && !filteredData?.length}
          pagination={data?.meta.pagination}
          onPageChange={handlePageChange}
          onEdit={handleEdit}
          onMarkAsPaid={handleMarkAsPaid}
          onDelete={handleDelete}
        />
      )}

      <TransactionFormModal
        isOpen={formDisclosure.isOpen}
        onClose={formDisclosure.onClose}
        mode={formMode}
        initialData={formMode === 'edit' ? selectedTransaction : null}
        onSubmit={handleFormSubmit}
        onStopRecurrence={handleStopRecurrence}
        isSubmitting={createMutation.isLoading || updateMutation.isLoading}
        clientsOptions={clientsOptions}
        providerOptions={providerOptions}
        employeeOptions={employeeOptions}
        paymentMethodOptions={paymentMethodOptions}
        paymentConditionOptions={paymentConditionOptions}
      />

      <MarkAsPaidModal
        isOpen={markPaidDisclosure.isOpen}
        onClose={markPaidDisclosure.onClose}
        transaction={selectedTransaction}
        isSubmitting={markAsPaidMutation.isLoading}
        paymentMethodOptions={paymentMethodOptions}
        paymentConditionOptions={paymentConditionOptions}
        onSubmit={handleMarkAsPaidSubmit}
      />

      <DeleteTransactionModal
        isOpen={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        isSubmitting={deleteMutation.isLoading}
        isRecurring={Boolean(selectedTransaction?.is_recurring)}
        onSubmit={handleDeleteSubmit}
      />
    </AnyBox>
  )
}
